// ***** Enums *****

// types of objects created by parsing
export enum PARSE_TYPE {
  TOKEN      = 'token',         // lexical tokens (atoms)
  EXPRESSION = 'expression',    // collection of tokens that form an expression
  TERM       = 'term',          // collection of tokens that form a term
}

// expression types
export enum EXPRESSION_TYPE {
  LIST = 'list',          // list of values for 'in' comparator
  MATH = 'math',          // arithmetic expression
}

// types of lexical tokens that compose a filter
export enum TOKEN_TYPE {

  // search filters
  AND                     = 'and',                     // && and
  BOOLEAN                 = 'boolean-value',           // true | false
  BY                      = 'by',                      // the 'by' keyword in a PQ 'group' or 'lookup' command
  COLON                   = 'colon',                   // isolated : (possibly part of a ternary expression)
  COMMA                   = 'comma',                   // can appear in lists or PQ commands
  COMMAND                 = 'command',                 // Power Query command
  COMMENT                 = 'comment',                 // Comment within a PQ
  COMPARISON              = 'comparison',              // comparison operator: = != contains ...
  FIELD                   = 'field',                   // a facet name
  FROM                    = 'from',                    // the 'from' keyword in a PQ 'parse' or 'lookup' command
  FUNCTION                = 'function',                // graphing function
  INTERVAL                = 'interval',                // time duration
  LEFT_PAREN              = 'left-paren',              // opening paren
  MATH_OPERATOR           = 'math-operator',           // arithmetic operator
  MODIFIER                = 'modifier',                // affects how an operator works; so far, just 'matchcase'
  NOT                     = 'not',                     // ! not
  NUMBER                  = 'number',                  // a number (can have a decimal point and underscores)
  ON                      = 'on',                      // the 'on' keyword in a PQ 'join' command
  OR                      = 'or',                      // || or
  PIPE                    = 'pipe',                    // |
  QUESTION_MARK           = 'question-mark',           // isolated ? (possibly part of a ternary expression)
  REGEX                   = 'regex',                   // regular expression
  RIGHT_PAREN             = 'right-paren',             // closing paren
  STRING                  = 'string',                  // a quoted string
  TIME_FUNCTION           = 'time-function',           // dayOfWeek, etc
  TIME_ZONE               = 'time-zone',               // argument for time functions
  TIMESHIFT               = 'timeshift',               // timeshift operator for plot queries
  UNKNOWN                 = 'unknown',                 // unrecognized
  VALUE                   = 'value',                   // facet value (used for autocomplete matching)
  WHERE                   = 'where',                   // the 'where' keyword in a graph function body
  WORD                    = 'word',                    // unquoted sequence of allowed characters starting with letter or colon

  // power queries
  PQ_AGGREGATION_FUNCTION = 'pq-aggregation-function', // PQ aggregation function, eg 'running_count()'
  PQ_COLUMN               = 'pq-column',               // aggregate type for a facet or PQ field
  PQ_EXPRESSION           = 'pq-expression',           // aggregate type for any type that can start an expression
  PQ_EXPRESSION_FUNCTION  = 'pq-expression-function',  // aggregate type for the four PQ functions (other than group)
  PQ_FIELD                = 'pq-field',                // a field created by a PQ command, eg 'let'
  PQ_GROUP_FUNCTION       = 'pq-group-function',       // PQ grouping function, eg 'sum'
  PQ_NETWORK_FUNCTION     = 'pq-network-function',     // PQ network function, eg 'net_ipv4()'
  PQ_NUMERIC_FUNCTION     = 'pq-numeric-function',     // PQ numeric function, eg 'floor()'
  PQ_STRING_FUNCTION      = 'pq-string-function',      // PQ string function, eg 'trim()'
  PQ_TIME_FUNCTION        = 'pq-time-function',        // PQ time function, eg 'timebucket()'
}

// filter states (used for autocomplete / search helper)
export enum FILTER_STATE {

  // states for completed tokens (some need to be followed by a separator)
  COMPARATOR          = 'comparator',          // got a comparison operator
  MODIFIED_COMPARATOR = 'modified_comparator', // got a comparison operator followed immediately by a colon
  DOLLAR              = 'dollar',              // got a $
  FIELD               = 'field',               // got a field
  FIELD_IN_FUNCTION   = 'field-in-function',   // got a field in a function body
  LIST                = 'list',                // got the opening ( for a list following 'in' operator
  NUMBER              = 'number',              // got a number
  PIPE                = 'pipe',                // got command character |
  START               = 'start',               // blank slate
  STRING              = 'string',              // got a quoted string
  TERM                = 'term',                // got a field-operator-value term
  TIMESHIFT_INTERVAL  = 'timeshift',           // got the 'timeshift' keyword and an interval
  WHERE               = 'where',               // got the 'where' keyword in a function body
  WORD                = 'word',                // got a word

  // various comparators
  COMPARATOR_EQUALITY = 'comparator-equality',    // = == !=
  COMPARATOR_NUMERIC  = 'comparator-numeric',     // > >= <= <
  COMPARATOR_CONTAINS = 'comparator-contains',    // 'contains'
  COMPARATOR_MATCHES  = 'comparator-matches',     // 'matches'
  COMPARATOR_IN       = 'comparator-in',          // 'in'

  // filter states where the last token is not complete
  IN_COMMAND      = 'in-command',         // in a PQ command
  IN_COMPARATOR   = 'in-comparator',
  IN_NUMBER       = 'in-number',
  IN_NUMBER_VALUE = 'in-number-value',    // in a number that is a right-side value
  IN_STRING       = 'in-string',
  IN_STRING_VALUE = 'in-string-value',    // in a quoted string that is a right-side value
  IN_SYSTEM_FIELD = 'in-system-field',    // $ followed by WORD character
  IN_WORD         = 'in-word',
  IN_WORD_START   = 'in-word-start',      // in a word at start of filter
  IN_WORD_VALUE   = 'in-word-value',      // in a word that is a right-side value (boolean)

  // Power Query states
  PQ_EXPRESSION_START = 'pq-expression-start',    // expecting a PQ expression
  PQ_GROUP            = 'pq-group',               // got a PQ group function ('group' only)
  PQ_GROUP_BY         = 'pq-group-by',            // got the 'group' command keyword 'by'
  PQ_JOIN_ON          = 'pq-join-on',             // got the 'join' command keyword 'on'
  PQ_SUBQUERIES       = 'pq-subqueries',          // got at least two subqueries in 'join' command
  PQ_TABLE            = 'pq-table',               // got a table name ('lookup' command)
  PQ_TABLE_COLUMN     = 'pq-table-column',        // got a table column assignment ('lookup' command)

  // Power Query states where the last token is not complete
  IN_PQ_COLUMN               = 'in-pq-column', // in a field or PQ field
  IN_PQ_EXPRESSION_START     = 'in-pq-expression-start',
  IN_PQ_AGGREGATION_FUNCTION = 'in-pq-aggregation-function',
  IN_PQ_GROUP_FUNCTION       = 'in-pq-group-function',
  IN_PQ_NETWORK_FUNCTION     = 'in-pq-numeric-function',
  IN_PQ_NUMERIC_FUNCTION     = 'in-pq-numeric-function',
  IN_PQ_STRING_FUNCTION      = 'in-pq-string-function',
  IN_PQ_TABLE_COLUMN         = 'in-pq-table-column',
  IN_PQ_TIME_FUNCTION        = 'in-pq-time-function',
}

// ***** Types *****

export type ANY_TYPE = TOKEN_TYPE | EXPRESSION_TYPE | PARSE_TYPE;

/**
 * A token is a recognizable atomic part of a Scalyr filter, based on the Scalyr query language. It is also the
 * base type for Expression and Term.
 */
export type Token = {
  start:     number;     // start index within filter
  end:       number;     // end index within filter
  text:      string;     // text content
  parseType: PARSE_TYPE; // token, expression, or term
  type:      ANY_TYPE;   // token type eg STRING | FIELD | FUNCTION
  complete?: boolean;    // true if the token is complete (eg a terminated string)
};

/**
 * An expression is a set of tokens that resolves to a single value, such as an arithmetic expression. It is
 * a subtype of Token.
 */

export type Expression = {
  start:     number;       // start index within filter
  end:       number;       // end index within filter
  text:      string;       // text content
  parseType: PARSE_TYPE;   // TERM
  type:      ANY_TYPE;     // expression type
  tokens:    Array<Token>; // for type EXPRESSION only
};

/**
 * A term is a part of a filter that will evaluate to true or false. It is a subtype of Expression and
 * takes the form of
 *
 *     [field] [comparator] [value]
 *
 * or a shortcut version of the above, such as a string with implied comparison to the 'message' field.
 *
 * Examples:
 *
 *     tag contains 'cpu'
 *     bytes > 5000
 *     'exception'
 */
export type Term = {
  start:     number;       // start index within filter
  end:       number;       // end index within filter
  text:      string;       // text content
  parseType: PARSE_TYPE;   // TERM
  type:      ANY_TYPE;     // term type; reflects the type of field
  tokens:    Array<Token>; // list of tokens that compose the term (only a boolean term lacks them)
  field:     string;       // left-side value
  operator:  string;       // comparator
  value:     string;       // right-side value
};

export type MaybeNode = Node | null | undefined;

/**
 * A node is a single element of an Abstract Syntax Tree (AST) that contains information
 * about its location in the tree.
 */
export type Node = {
  type?:     ANY_TYPE;  // optional node type
  token:     Token;     // node content
  parent:    MaybeNode; // parent (only the root node lacks a parent)
  child:     MaybeNode; // child (of a NOT node)
  left:      MaybeNode; // left child (of an AND or OR node)
  right:     MaybeNode; // right child (of an AND or OR node)
  REMOVE:    boolean;   // flag used for pruning AST
};

/**
 * Result object for removeConstrainingTerms().
 */
export type AdjustedQuery = {
  terms:  Array<Token>;    // terms that were removed from a filter
  filter: string;          // the updated filter
};

/**
 * Object comprising state at the end of a filter string, describing what might come next
 */
export type FilterState = {
  states:      FILTER_STATE[];    // states of the filter, based on what has come so far
  valid:       TOKEN_TYPE[];      // list of valid token types that can come next
  text:        string;            // text that was used to determine state; can be replaced by completion value later
  field:       string;            // current field name, in case we are expecting a value
  op:          string;            // current operator
  pqFields?:   string[];          // list of fields created (eg by 'let') within a PQ
  isNumeric?:  boolean;           // current context is numeric
  isString?:   boolean;           // current context is string
  tokens?:     Token[];           // list of tokens
  listValues?: string[];          // values in list following 'in'
};

/**
 * Object with information about an autocomplete match.
 */
export type Match = {
  type:            TOKEN_TYPE;    // token type
  text:            string;        // text content
  completionValue: string;        // value to use if user chooses to complete this match
};

/**
 * Object summarizing autocomplete matching results
 */
export type MatchInfo = {
  matches:     Record<TOKEN_TYPE, Match[]>;
  matchedText: string;
};

export type AddTermToFilterParams = {
  filter:          string;
  field?:          string;
  op?:             string;
  value?:          string;
  text?:           string;
  negate?:         boolean;
  addParentheses?: boolean;
};

// denotes the range of tokens that compose a subquery in a 'join' or 'union' command
export type Subquery = {
  start: number;    // index of starting token
  end?:  number;    // one beyond index of last token
};
